<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <!-- header -->
  <div class="small">
    <NuxtLink id="back-page-link" class="link-sm" :to="backPageLink"> Back to Order Details </NuxtLink>
  </div>
  <h1 class="margin-top-xxs" tabindex="0" v-text="config.RETURN_ITEMS_TITLE"></h1>
  <!-- content -->
  <div class="grid-x">
    <div class="small-12 large-7">
      <Notification
        v-for="(notification, index) in returnItemsStore.GET_RETURN_NOTIFICATION"
        :key="index"
        class="small-margin-top-s large-margin-top-m small-margin-bottom-z large-margin-bottom-z"
        :class="notification?.class"
        :severity="notification?.severity"
        :closable="false"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="notification.text"></div>
      </Notification>
      <template v-if="returnItemsStore.returnItems && !returnItemsStore.loading.getReturnItems">
        <ReturnItems class="small-margin-top-s large-margin-top-m" />
        <RefundOptions />
        <ShippingAddressSection v-if="returnItemsStore.SHOW_SHIPPING_ADDRESS_SECTION" />
        <ReturnMethod />
      </template>
    </div>
    <div
      v-if="returnItemsStore.returnItems && !returnItemsStore.loading.getReturnItems"
      id="return-summary"
      class="small-12 large-4 large-offset-1 margin-bottom-l"
      :class="{
        'large-padding-top-xs large-margin-top-xxxl': returnItemsStore.GET_RETURN_NOTIFICATION.length,
      }"
    >
      <RefundDetails />
      <SubmitReturn />
    </div>
  </div>
  <Skeleton v-if="returnItemsStore.loading.getReturnItems" />
</template>

<script setup>
// Imports
import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { Experimentation } from '@vue-util/experimentation';
import { UserStatus } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';

// Local components
import ReturnItems from '@/components/returns/ReturnItems.vue';
import RefundOptions from '@/components/returns/RefundOptions.vue';
import ReturnMethod from '@/components/returns/ReturnMethod.vue';
import RefundDetails from '@/components/returns/RefundDetails.vue';
import ShippingAddressSection from '@/components/returns/ShippingAddressSection.vue';
import SubmitReturn from '@/components/returns/SubmitReturn.vue';
import Skeleton from '@/components/skeleton/ReturnItemsView.vue';

// Utility functions
import { getLoyaltyStatus } from '@/utils/UserDetails';
import { fireApiErrorTag } from '@/utils/returns/General';
import { handleSessionTimeout } from '@/utils/SessionHandler';

// Config
import * as config from 'config';

// Pinia Store & Composables
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useJoinStarRewardsStore } from '@/stores/useJoinStarRewardsStore';
const returnItemsStore = useReturnItemStore();
const joinStarRewardsStore = useJoinStarRewardsStore();
const { isBcom } = useBrand();
const route = useRoute();

// SSR Data Fetching
const { data: ssrData } = await useAsyncData(`return-items-${route.query.o}`, async () => {
  const { cookie, 'user-agent': userAgent } = useRequestHeaders(['cookie', 'user-agent']);
  return await returnItemsStore.GET_RETURN_ITEMS({
    token: route.query.o,
    upc: route.query.upcnumber,
    ssrContext: { cookie, userAgent },
  });
});

// Computed Properties
const backPageLink = computed(() => {
  const ot = route.query?.o;
  const orderToken = ot && `?o=${encodeURIComponent(ot)}`;
  return `/purchases/online/${returnItemsStore.returnItems?.orderNumber}${orderToken}`;
});

// Lifecycle Hooks
onMounted(async () => {
  const recipeIds = await Experimentation.getExpIds();
  fireTag('view', {
    page_name: 'return2.0',
    page_type: 'order history',
    site_action: 'items to return start',
    action_location: 'order details',
    experimentation_ids: recipeIds,
  });

  if (returnItemsStore.returnItems?.conditionalReturnFeeEnabled && UserStatus.isSignedIn() && !getLoyaltyStatus()) {
    await joinStarRewardsStore.FETCH_STAR_REWARDS_SUMMARY();
  }

  if (isBcom && returnItemsStore.returnItems && !returnItemsStore.returnItems.cartonLines?.length) {
    fireTag('link', {
      event_name: 'PPE error',
      error_category: 'returns landing page',
      error_code: 'return2.0',
      error_label: ['empty carton lines'],
      error_message: [config.RETURN_EMPTY_CARTON_LINE],
      error_misc: '',
      error_scope: 'section',
    });
  }

  // 1. Fire the API error tag immediately if an error occurred during SSR.
  // We check ssrData.value to see if the server pass returned an error.
  if (ssrData.value) {
    fireApiErrorTag(ssrData.value);
    handleSessionTimeout(ssrData.value.errorCode);
  }
});

onUnmounted(() => {
  returnItemsStore.CLEAR_RETURN_ITEMS_DETAILS();
});
</script>

<style lang="scss" scoped>
#return-summary {
  position: sticky;
  top: 0px;
  height: max-content;
}
</style>
